import { useLang } from '@/lib/lang-context';
import { LANG_NAMES, type LangCode } from '@/lib/supported-langs';
import { FlagIcon } from '@/components/FlagIcon';
import classes from './LangSwitcher.module.css';

import type { JSX } from "react";

export interface LangSwitcherProps {
  /**
   * Controlled active language. Provide together with `onChange` to drive the
   * switcher from LOCAL state (e.g. a form's editing-language) instead of the
   * global UI-language context. Omit both to use the global `useLang()` context.
   */
  value?: LangCode;
  /** Controlled change handler (pair with `value`). */
  onChange?: (code: LangCode) => void;
  /**
   * Optional per-language completeness flags. When provided, each option shows a
   * ✓ (complete) / ✗ (incomplete) marker and the accessible name carries the
   * status so it is not conveyed by colour alone.
   */
  completeness?: Partial<Record<LangCode, boolean>>;
  /**
   * Accessible name for the switcher group (the `tablist`). Defaults to
   * `'Language'` (header/global usage); override it when the switcher drives a
   * more specific choice, e.g. `'SEO description language'`.
   */
  ariaLabel?: string;
}

export function LangSwitcher({
  value,
  onChange,
  completeness,
  ariaLabel = 'Language',
}: LangSwitcherProps = {}): JSX.Element {
  const ctx = useLang();
  // Controlled when both value + onChange are supplied; otherwise fall back to
  // the global UI-language context (header usage).
  const lang = value ?? ctx.lang;
  const setLang = onChange ?? ctx.setLang;

  return (
    <div className={classes.langPills} role="tablist" aria-label={ariaLabel}>
      {ctx.languages.map((code) => {
        const active = code === lang;
        const complete = completeness?.[code];
        const statusLabel =
          complete === undefined ? '' : complete ? ' — complete' : ' — incomplete';
        return (
          <button
            key={code}
            role="tab"
            type="button"
            aria-selected={active}
            aria-label={`${LANG_NAMES[code]} (${code})${statusLabel}`}
            disabled={active}
            onClick={() => setLang(code)}
            className={`${classes.langPill} ${active ? classes.langPillActive : ''}`}
          >
            <FlagIcon lang={code} size={13} />
            <span>{code.toUpperCase()}</span>
            {complete !== undefined && (
              <span
                className={classes.langPillStatus}
                aria-hidden="true"
                style={{ color: complete ? 'var(--w-green, #3f7d5b)' : 'var(--w-rust, #b03a2e)' }}
              >
                {complete ? (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="m20 6-11 11-5-5" /></svg>
                ) : (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                )}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
